<template>
  <div>
    <div class="modal fade" id="CreatorListModal" ref="modalEle" tabindex="-1" role="dialog"
      aria-labelledby="modelTitleId" aria-hidden="true">
      <div class="modal-dialog" :class="productViewMode === 'details' ? 'modal-xl' : 'modal-full-width'"
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Kreator zamówienia stali</h5>
            <button type="button" class="close ms-auto" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-end mb-3">
                <div>
                  <label class="form-label mb-1">Zapisane zapotrzebowania</label>
                  <div class="text-muted small">
                    Znaleziono: {{ filteredSavedLists.length }}
                  </div>
                </div>

                <div style="width: 350px;">
                  <input v-model="savedListsSearch" type="text" class="form-control" placeholder="Szukaj..."
                    :disabled="loadingSavedLists">
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0">
                    <thead class="table-light">
                      <tr>
                        <th class="text-center" style="width: 70px;">ID</th>
                        <th style="width: 160px;">Nr zapotrzebowania</th>
                        <th>Kategoria</th>
                        <th>Utworzył</th>
                        <th style="width: 140px;">Data utworzenia</th>
                        <th class="text-end" style="width: 130px;">Akcja</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-if="loadingSavedLists">
                        <td colspan="6" class="text-center py-5">
                          <div class="spinner-border spinner-border-sm me-2"></div>
                          Ładowanie...
                        </td>
                      </tr>

                      <tr v-else-if="!paginatedSavedLists.length">
                        <td colspan="6" class="text-center text-muted py-5">
                          Brak zapisanych zapotrzebowań.
                        </td>
                      </tr>

                      <tr v-for="creator in paginatedSavedLists" v-else :key="creator.id"
                        :class="{ 'table-primary': selectedCreatorId === String(creator.id) }">
                        <td class="text-center fw-semibold">
                          {{ creator.id }}
                        </td>

                        <td>
                          <span class="fw-semibold">
                            {{ creator.zapotrzebowanie_id || '' }}
                          </span>
                        </td>

                        <td>
                          {{ getCategoryName(creator.kategoria) }}
                        </td>

                        <td>
                          {{ creator.created_by_name || 'Nieznany użytkownik' }}
                        </td>

                        <td>
                          <span class="text-muted">
                            {{ formatDate(creator.created_at) }}
                          </span>
                        </td>

                        <td class="text-end">
                          <div class="d-flex justify-content-end gap-1">
                            <button type="button" class="btn btn-sm btn-outline-primary"
                              :disabled="loadingSavedList || deletingCreator" @click="loadSavedList(creator.id)">
                              <span v-if="loadingSavedList && selectedCreatorId === String(creator.id)"
                                class="spinner-border spinner-border-sm me-1"></span>
                              <i v-else class="bi bi-pencil-square me-1"></i>Wczytaj
                            </button>

                            <button v-if="!creator.inventory_status_id" type="button"
                              class="btn btn-sm btn-outline-danger" :disabled="deletingCreator"
                              @click="deleteCreator(creator)">
                              <span v-if="deletingCreator && selectedCreatorId === String(creator.id)"
                                class="spinner-border spinner-border-sm"></span>

                              <i v-else class="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="savedListsTotalPages > 1"
                  class="card-footer bg-white border-top d-flex justify-content-between align-items-center">
                  <div class="small text-muted">
                    Wyświetlane {{ ((savedListsPage - 1) * savedListsPerPage) + 1 }}–{{ Math.min(savedListsPage *
                      savedListsPerPage, filteredSavedLists.length) }}
                    z {{ filteredSavedLists.length }}
                  </div>

                  <nav aria-label="Paginacja zapotrzebowań">
                    <ul class="pagination pagination-sm mb-0">
                      <li class="page-item" :class="{ disabled: savedListsPage === 1 }">
                        <button type="button" class="page-link" :disabled="savedListsPage === 1"
                          @click="savedListsPage--">
                          <i class="bi bi-chevron-left"></i>
                        </button>
                      </li>

                      <li v-for="page in savedListsTotalPages" :key="page" class="page-item"
                        :class="{ active: savedListsPage === page }">
                        <button type="button" class="page-link" @click="savedListsPage = page">
                          {{ page }}
                        </button>
                      </li>

                      <li class="page-item" :class="{ disabled: savedListsPage === savedListsTotalPages }">
                        <button type="button" class="page-link" :disabled="savedListsPage === savedListsTotalPages"
                          @click="savedListsPage++">
                          <i class="bi bi-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            <div v-if="selectedCreatorId" class="border-top pt-4 mt-4">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h6 class="mb-0">
                  Kreator zapotrzebowania: {{ selectedRequirementNumber }}
                </h6>

                <button type="button" class="btn btn-sm btn-outline-secondary" @click="cancelEdit">
                  <i class="bi bi-x-lg me-1"></i>
                  Anuluj
                </button>
              </div>

              <div class="row mb-4">
                <div class="col-md-3">
                  <label class="form-label">Kategoria</label>

                  <select v-model="selectedCategory" class="form-select" :disabled="!!inventoryStatusId"
                    @change="loadArticles">
                    <option value="">Wybierz kategorię</option>

                    <option v-for="category in kreator_kategorie" :key="category.category_id"
                      :value="category.category_id">
                      {{ category.name }}
                    </option>
                  </select>
                </div>

                <div class="col-md-9">
                  <label class="form-label">Artykuł</label>

                  <select v-model="selectedArticle" class="form-select"
                    :disabled="!!inventoryStatusId || !selectedCategory || loadingArticles" @change="articleChanged">
                    <option value="">
                      {{ loadingArticles ? 'Ładowanie...' : 'Wybierz artykuł' }}
                    </option>

                    <option v-for="article in articles" :key="article.id" :value="article.id">
                      {{ article.catalog_number }},
                      {{ article.model }},
                      {{ article.description }}
                      {{ article.details }}
                    </option>
                  </select>
                </div>
              </div>

              <div v-if="selectedArticle" class="mt-3">
                <h6 class="mb-3">Odcinki</h6>

                <div class="table-responsive">
                  <table class="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th style="width: 70px;">#</th>
                        <th>Długość w mm (bez naddatków)</th>
                        <th>Ilość szt.</th>
                        <th style="width: 55px;"></th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-for="(item, index) in orderItems" :key="item.id">
                        <td class="align-middle">
                          {{ index + 1 }}
                        </td>

                        <td>
                          <input :ref="el => setLengthRef(el, index)" v-model="item.length" type="number" min="1"
                            step="1" class="form-control" placeholder="Długość"
                            @keydown.tab="handleLengthTab(index, $event)">

                        </td>

                        <td>
                          <input :ref="el => setQuantityRef(el, index)" v-model="item.quantity" type="number" min="1"
                            step="1" class="form-control" placeholder="Ilość"
                            @keydown.tab="handleQuantityTab(index, $event)">
                        </td>

                        <td class="text-center align-middle">
                          <div class="d-flex justify-content-center gap-1">
                            <button v-if="index === orderItems.length - 1" type="button" class="btn btn-sm btn-primary"
                              title="Dodaj pozycję" @click="addOrderItem">
                              <i class="bi bi-plus-lg"></i>
                            </button>

                            <button v-if="orderItems.length > 1 && !inventoryStatusId" type="button"
                              class="btn btn-sm btn-outline-danger" title="Usuń pozycję"
                              :disabled="deletingItemId === item.id" @click="removeOrderItem(item, index)">
                              <span v-if="deletingItemId === item.id" class="spinner-border spinner-border-sm"></span>

                              <i v-else class="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="inventoryStatusId" class="alert alert-danger mt-3 mb-0">
                <i class="bi bi-exclamation-triangle-fill me-2"></i> <strong>To zapotrzebowanie ma zmieniony
                  status.</strong> Nie można go edytować ani usuwać. Można jedynie wygenerować nowy plan rozkroju bez
                zapisywania zmian.
              </div>
            </div>

            <div v-if="saveMessage" class="alert alert-success mt-3 mb-0">
              <i class="bi bi-check-circle me-2"></i>
              {{ saveMessage }}
            </div>

            <div v-if="saveError" class="alert alert-danger mt-3 mb-0">
              <i class="bi bi-exclamation-triangle me-2"></i>
              {{ saveError }}
            </div>
          </div>

          <div class="modal-footer">

            <button v-if="inventoryStatusId" type="button" class="btn btn-outline-success" :disabled="generatingPdf"
              @click="generateCuttingPlanPdf">
              <span v-if="generatingPdf" class="spinner-border spinner-border-sm me-2" role="status"
                aria-hidden="true"></span>
              <i v-else class="bi bi-file-earmark-pdf me-2"></i> {{ generatingPdf ? 'Generowanie...' : 'Pobierz rozkrój'
              }}
            </button>
            <button v-if="selectedCreatorId && !inventoryStatusId" type="button" class="btn btn-primary"
              :disabled="saving" @click="saveOrder">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>

              <i v-else class="bi bi-save me-2"></i>

              {{ saving ? 'Zapisywanie...' : 'Zapisz zmiany' }}
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bi bi-x-square me-2"></i>
              Zamknij
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import axios from "axios";

export default {
  props: {
    project: [Number, String],
    team: {
      type: String,
      default: ''
    },
    liczba_maszyn: {
      type: Number,
      default: 1
    },
    kreator_kategorie: {
      type: Array,
      default: () => []
    },
    context: String
  },

  data() {
    return {
      modalObj: null,
      inventoryStatusId: null,
      selectedCreatorId: '',
	  selectedRequirementNumber: '',
      savedLists: [],
      savedListsSearch: '',
      savedListsPage: 1,
      savedListsPerPage: 5,
      loadingSavedLists: false,
      loadingSavedList: false,
      selectedCategory: '',
      selectedArticle: '',
      articles: [],
      loadingArticles: false,
      saving: false,
      saveMessage: '',
      saveError: '',
      deletingItemId: null,
      deletingCreator: false,
      orderItems: [
        {
          id: 1,
          length: '',
          quantity: ''
        }
      ],
      lengthRefs: [],
      quantityRefs: []
    };
  },

  computed: {
    filteredSavedLists() {
      const search = this.savedListsSearch.trim().toLowerCase();

      if (!search) {
        return this.savedLists;
      }

      return this.savedLists.filter(creator => {
        const values = [
          creator.id,
          creator.zapotrzebowanie_id,
          creator.inventory_status_id,
          this.getCategoryName(creator.kategoria),
          creator.created_at,
          this.formatDate(creator.created_at),
          creator.created_by_name
        ];

        return values.some(value =>
          String(value ?? '').toLowerCase().includes(search)
        );
      });
    },

    savedListsTotalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredSavedLists.length / this.savedListsPerPage)
      );
    },

    paginatedSavedLists() {
      const start =
        (this.savedListsPage - 1) * this.savedListsPerPage;

      return this.filteredSavedLists.slice(
        start,
        start + this.savedListsPerPage
      );
    }
  },

  watch: {
    savedListsSearch() {
      this.savedListsPage = 1;
    },

    filteredSavedLists() {
      if (this.savedListsPage > this.savedListsTotalPages) {
        this.savedListsPage = this.savedListsTotalPages;
      }
    }
  },

  mounted() {
    this.modalObj = new Modal(this.$refs.modalEle);

    this.$refs.modalEle.addEventListener('show.bs.modal', () => {
      this.loadSavedLists();
    });

    this.$refs.modalEle.addEventListener('hide.bs.modal', () => {
      this.resetForm();
    });
  },

  methods: {

async generateCuttingPlanPdf() {
  this.saveMessage = '';
  this.saveError = '';

  if (!this.selectedCategory) {
    this.$emit('added-error', 'Wybierz kategorię.');
    return;
  }

  if (!this.selectedArticle) {
    this.$emit('added-error', 'Wybierz artykuł.');
    return;
  }

  const items = this.orderItems
    .filter(item =>
      item.length !== '' &&
      item.length !== null &&
      item.quantity !== '' &&
      item.quantity !== null
    )
    .map(item => ({
      length: Number(item.length),
      quantity: Number(item.quantity)
    }));

  if (!items.length) {
    this.$emit(
      'added-error',
      'Wprowadź przynajmniej jeden kompletny odcinek.'
    );
    return;
  }

  const invalidItem = items.some(item =>
    !Number.isInteger(item.length) ||
    item.length <= 0 ||
    !Number.isInteger(item.quantity) ||
    item.quantity <= 0
  );

  if (invalidItem) {
    this.$emit(
      'added-error',
      'Długość i ilość muszą być liczbami całkowitymi większymi od zera.'
    );
    return;
  }

  this.generatingPdf = true;

  try {
    const response = await axios.post(
      this.route('creator.data.cutting-plan.pdf'),
      {
        project_id: this.project,
        team_name: this.team,
        liczba_maszyn: this.liczba_maszyn,
        kategoria: this.selectedCategory,
        article_id: this.selectedArticle,
        zapotrzebowanie_id: this.selectedRequirementNumber,
        items: items
      }
    );

    const files = response.data?.files;

    if (!Array.isArray(files) || files.length === 0) {
      throw new Error('Nie otrzymano plików PDF do pobrania.');
    }

    files.forEach(file => {
      if (!file.filename || !file.content) {
        return;
      }

      const binary = atob(file.content);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob(
        [bytes],
        { type: 'application/pdf' }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = file.filename;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    });

  } catch (error) {
    console.error(error);

    let message =
      error.message ||
      'Wystąpił błąd podczas generowania rozkroju.';

    const data = error.response?.data;

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);

        if (parsed.errors) {
          message = Object.values(parsed.errors).flat().join(' ');
        } else if (parsed.message) {
          message = parsed.message;
        }
      } catch (e) {
        // Pozostawiamy komunikat domyślny.
      }
    } else if (data?.errors) {
      message = Object.values(data.errors).flat().join(' ');
    } else if (data?.message) {
      message = data.message;
    }

    this.saveError = message;
    this.$emit('added-error', message);
  } finally {
    this.generatingPdf = false;
  }
},
    async removeOrderItem(item, index) {
      this.saveMessage = '';
      this.saveError = '';
      if (this.inventoryStatusId) {
        this.saveError = 'To zapotrzebowanie ma zmieniony status i nie może być edytowane.';
        return;
      }
      if (this.orderItems.length <= 1) {
        this.saveError = 'Rekord musi zawierać przynajmniej jedną pozycję.';
        return;
      }

      const result = await Toast.fire({
        toast: false,
        icon: 'warning',
        title: 'Usuwanie pozycji',
        html: 'Czy na pewno chcesz usunąć wybraną pozycję?',
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Tak',
        denyButtonText: 'Nie',
        timer: false,
        position: 'center'
      });

      if (!result.isConfirmed) {
        return;
      }

      if (
        !this.selectedCreatorId ||
        !item.id ||
        !Number.isInteger(Number(item.id))
      ) {
        this.orderItems.splice(index, 1);

        this.$nextTick(() => {
          this.focusLength(
            Math.max(0, Math.min(index, this.orderItems.length - 1))
          );
        });

        return;
      }

      this.deletingItemId = item.id;

      try {
        await axios.delete(
          this.route('creator.data.item.delete', {
            id: this.selectedCreatorId,
            itemId: item.id
          }),
          {
            data: {
              project_id: this.project,
              team_name: this.team
            }
          }
        );

        this.orderItems.splice(index, 1);

        this.lengthRefs = [];
        this.quantityRefs = [];

        this.saveMessage = 'Pozycja została usunięta.';
        this.saveError = '';

        Toast.fire({
          toast: true,
          icon: 'success',
          title: 'Pozycja została usunięta.',
          position: 'top-end',
          timer: 2500,
          showConfirmButton: false
        });

        this.$nextTick(() => {
          this.focusLength(
            Math.max(0, Math.min(index, this.orderItems.length - 1))
          );
        });
      } catch (error) {
        console.error(error);

        let message = 'Nie udało się usunąć pozycji.';

        if (error.response?.data?.message) {
          message = error.response.data.message;
        }

        this.saveError = message;
        this.saveMessage = '';

        Toast.fire({
          toast: true,
          icon: 'error',
          title: message,
          position: 'top-end',
          timer: 3500,
          showConfirmButton: false
        });
      } finally {
        this.deletingItemId = null;
      }
    },

    async deleteCreator(creator) {
      if (!creator?.id) {
        return;
      }

      const result = await Toast.fire({
        toast: false,
        icon: 'warning',
        title: 'Usuwanie zapotrzebowania',
        html: `Czy na pewno chcesz usunąć całe zapotrzebowanie #${creator.id}?`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Tak',
        denyButtonText: 'Nie',
        timer: false,
        position: 'center'
      });

      if (!result.isConfirmed) {
        return;
      }

      this.selectedCreatorId = String(creator.id);
      this.deletingCreator = true;
      this.saveMessage = '';
      this.saveError = '';

      try {
        const response = await axios.delete(
          this.route('creator.data.delete', creator.id),
          {
            data: {
              project_id: this.project,
              team_name: this.team
            }
          }
        );

        this.saveMessage = response.data.message;
        this.saveError = '';

        if (this.selectedCreatorId === String(creator.id)) {
          this.cancelEdit();
        }

        await this.loadSavedLists();

        Toast.fire({
          toast: true,
          icon: 'success',
          title: response.data.message || 'Zapotrzebowanie zostało usunięte.',
          position: 'top-end',
          timer: 2500,
          showConfirmButton: false
        });

        this.$emit('added-article');
        this.$emit(
          'added-success',
          'Zapotrzebowanie zostało usunięte.'
        );
      } catch (error) {
        console.error(error);

        let message = 'Nie udało się usunąć zapotrzebowania.';

        if (error.response?.data?.message) {
          message = error.response.data.message;
        }

        this.saveError = message;
        this.saveMessage = '';

        Toast.fire({
          toast: true,
          icon: 'error',
          title: message,
          position: 'top-end',
          timer: 3500,
          showConfirmButton: false
        });

        this.$emit('added-error', message);
      } finally {
        this.deletingCreator = false;
      }
    },

    async loadSavedLists() {
      this.loadingSavedLists = true;
      this.saveError = '';

      try {
        const response = await axios.get(
          this.route('creator.data.list'),
          {
            params: {
              project_id: this.project,
              team_name: this.team
            }
          }
        );

        this.savedLists = response.data;
        this.savedListsPage = 1;
      } catch (error) {
        console.error(error);
        this.savedLists = [];
        this.saveError = 'Nie udało się pobrać zapisanych zapotrzebowań.';
      } finally {
        this.loadingSavedLists = false;
      }
    },

    getCategoryName(categoryId) {
      const category = this.kreator_kategorie.find(
        item => String(item.category_id) === String(categoryId)
      );

      return category
        ? category.name
        : `Kategoria #${categoryId}`;
    },

    formatDate(date) {
      if (!date) {
        return '';
      }

      const parsedDate = new Date(date);

      if (Number.isNaN(parsedDate.getTime())) {
        return date;
      }

      return parsedDate.toLocaleString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async loadSavedList(id) {
      if (!id) {
        return;
      }
      this.selectedCreatorId = String(id);
      this.loadingSavedList = true;
      this.saveMessage = '';
      this.saveError = '';
      try {
        const response = await axios.get(
          this.route('creator.data.show', id)
        );
        const creator = response.data;
        this.inventoryStatusId = creator.inventory_status_id ?? null;
        this.selectedCategory = String(creator.kategoria);
		this.selectedRequirementNumber = creator.zapotrzebowanie_id ?? '';
        await this.loadArticles();
        this.selectedArticle = String(creator.article_id);
        this.orderItems = creator.items.map((item, index) => ({
          id: item.id,
          length: item.dlugosc,
          quantity: item.ilosc
        }));

        this.orderItems.push({
          id: Date.now() + Math.random(),
          length: '',
          quantity: ''
        });

        this.lengthRefs = [];
        this.quantityRefs = [];

        this.$nextTick(() => {
          this.focusLength(0);
        });
      } catch (error) {
        console.error(error);
        this.selectedCreatorId = '';
        this.saveError = 'Nie udało się wczytać zapisanego zapotrzebowania.';
      } finally {
        this.loadingSavedList = false;
      }
    },

    cancelEdit() {
      this.selectedCreatorId = '';
	  this.selectedRequirementNumber = '';
      this.selectedCategory = '';
      this.selectedArticle = '';
      this.articles = [];
      this.resetOrderItems();
      this.saveMessage = '';
      this.saveError = '';
      this.inventoryStatusId = null;
    },

    async loadArticles() {
      this.selectedArticle = '';
      this.articles = [];
      this.saveMessage = '';
      this.saveError = '';

      if (!this.selectedCategory) {
        return;
      }

      this.loadingArticles = true;

      try {
        const response = await axios.get(
          this.route(
            'creator.settings.categories.articles',
            this.selectedCategory
          )
        );

        this.articles = response.data;
      } catch (error) {
        console.error(error);
        this.articles = [];
        this.saveError = 'Nie udało się pobrać listy artykułów.';
      } finally {
        this.loadingArticles = false;
      }
    },

    articleChanged() {
      this.saveMessage = '';
      this.saveError = '';

      if (this.selectedArticle) {
        this.$nextTick(() => {
          this.focusLength(0);
        });
      }
    },

    async saveOrder() {
      this.saveMessage = '';
      this.saveError = '';
      if (this.inventoryStatusId) {
        this.saveError = 'To zapotrzebowanie ma zmieniony status i nie może być zapisane. Można jedynie wygenerować plan rozkroju.';
        return;
      }
      if (!this.selectedCreatorId) {
        this.$emit('added-error', 'Wybierz zapotrzebowanie do edycji.');
        return;
      }

      if (!this.selectedCategory) {
        this.$emit('added-error', 'Wybierz kategorię.');
        return;
      }

      if (!this.selectedArticle) {
        this.$emit('added-error', 'Wybierz artykuł.');
        return;
      }

      const items = this.orderItems
        .filter(item =>
          item.length !== '' &&
          item.length !== null &&
          item.quantity !== '' &&
          item.quantity !== null
        )
        .map(item => ({
          length: Number(item.length),
          quantity: Number(item.quantity)
        }));

      if (!items.length) {
        this.$emit(
          'added-error',
          'Wprowadź przynajmniej jeden kompletny odcinek.'
        );
        return;
      }

      const invalidItem = items.some(item =>
        !Number.isInteger(item.length) ||
        item.length <= 0 ||
        !Number.isInteger(item.quantity) ||
        item.quantity <= 0
      );

      if (invalidItem) {
        this.$emit(
          'added-error',
          'Długość i ilość muszą być liczbami całkowitymi większymi od zera.'
        );
        return;
      }

      this.saving = true;

      try {
        const response = await axios.put(
          this.route('creator.data.update', this.selectedCreatorId),
          {
            project_id: this.project,
            team_name: this.team,
            liczba_maszyn: this.liczba_maszyn,
            kategoria: this.selectedCategory,
            article_id: this.selectedArticle,
            items: items
          }
        );

        this.saveMessage = response.data.message;
        this.saveError = '';

        await this.loadSavedLists();

        this.$emit('added-article');
        this.$emit(
          'added-success',
          'Zapotrzebowanie zostało poprawnie zaktualizowane.'
        );
        this.$emit('close');
        this.hide();
      } catch (error) {
        console.error(error);

        let message = 'Wystąpił błąd podczas aktualizowania zapotrzebowania.';

        if (error.response?.data?.errors) {
          message = Object.values(error.response.data.errors)
            .flat()
            .join(' ');
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        }

        this.saveError = message;
        this.saveMessage = '';

        this.$emit('added-error', message);
      } finally {
        this.saving = false;
      }
    },

    resetOrderItems() {
      this.orderItems = [
        {
          id: 1,
          length: '',
          quantity: ''
        }
      ];

      this.lengthRefs = [];
      this.quantityRefs = [];
    },

    addOrderItem() {
      const lastItem = this.orderItems[this.orderItems.length - 1];

      if (
        !lastItem ||
        lastItem.length === '' ||
        lastItem.length === null ||
        lastItem.quantity === '' ||
        lastItem.quantity === null
      ) {
        this.focusLength(this.orderItems.length - 1);
        return;
      }

      this.orderItems.push({
        id: Date.now() + Math.random(),
        length: '',
        quantity: ''
      });

      this.$nextTick(() => {
        this.focusLength(this.orderItems.length - 1);
      });
    },

    resetForm() {
      this.selectedCreatorId = '';
	  this.selectedRequirementNumber = '';
      this.savedLists = [];
      this.savedListsSearch = '';
      this.savedListsPage = 1;
      this.selectedCategory = '';
      this.selectedArticle = '';
      this.articles = [];
      this.loadingArticles = false;
      this.loadingSavedLists = false;
      this.loadingSavedList = false;
      this.saving = false;
      this.deletingItemId = null;
      this.deletingCreator = false;
      this.saveMessage = '';
      this.saveError = '';
      this.resetOrderItems();
      this.inventoryStatusId = null;
    },

    setLengthRef(el, index) {
      if (el) {
        this.lengthRefs[index] = el;
      }
    },

    setQuantityRef(el, index) {
      if (el) {
        this.quantityRefs[index] = el;
      }
    },

    focusLength(index) {
      this.$nextTick(() => {
        const input = this.lengthRefs[index];

        if (input) {
          input.focus();
        }
      });
    },

    handleLengthTab(index, event) {
      if (event.shiftKey) {
        return;
      }

      event.preventDefault();

      this.$nextTick(() => {
        const input = this.quantityRefs[index];

        if (input) {
          input.focus();
        }
      });
    },

    handleQuantityTab(index, event) {
      if (event.shiftKey) {
        return;
      }

      if (index === this.orderItems.length - 1) {
        event.preventDefault();
        this.addOrderItem();
      }
    },

    show() {
      if (this.modalObj) {
        this.modalObj.show();
      }
    },

    hide() {
      if (this.modalObj) {
        this.modalObj.hide();
      }
    }
  }
};
</script>

<style scoped>
h6 {
  font-size: 13px;
}
</style>